import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core'
import { FieldsModel } from '@syncfusion/ej2-angular-dropdowns'
import { groupBy } from 'ramda'
import { DropdownTreeDatum } from './dropdown-tree.model'

@Component({
  selector: 'app-dropdown-tree',
  templateUrl: './dropdown-tree.component.html',
  styleUrls: ['./dropdown-tree.component.scss'],
})
export class DropdownTreeComponent<T> implements OnChanges {
  fields: FieldsModel
  value: string[] // value input expects an array
  itemTemplate = '<div>${itemText}</div>'
  @Input() data: T[]
  @Input() datum: T
  @Input() groupByField: keyof T
  /** supports one to many fields that get joined to display full text */
  @Input() textField: keyof T | (keyof T)[]
  @Input() valueField: keyof T
  @Input() placeholder?: string
  @Input() sort?: (a: T, b: T) => number

  @Output() select = new EventEmitter<string>() // id of datum

  ngOnChanges(changes: SimpleChanges): void {
    if (this.data.length > 0 && !this.fields) {
      this.fields = this.getFields()
    }
    if (changes.datum && this.datum) {
      this.value = this.getValue()
    }
  }

  private getFields(): FieldsModel {
    const dataSource = this.getGroupedData()
    return {
      dataSource,
      value: 'value',
      text: 'selectedText',
      child: 'child',
    }
  }

  private getValue(): string[] {
    if (!this.datum || !this.valueField) {
      return []
    }
    return [String(this.datum[this.valueField])]
  }

  private getGroupedData(): DropdownTreeDatum[] {
    if (!this.groupByField || !this.data) {
      return []
    }
    if (this.sort) {
      this.data.sort(this.sort)
    }
    const grouped = groupBy((item: T) => item[this.groupByField] as string, this.data)
    return Object.entries(grouped).map((entry) => {
      const groupName = entry[0]
      const items = entry[1]
      const groupDatum: DropdownTreeDatum = {
        value: `group-${groupName}`,
        itemText: groupName,
        selectedText: groupName,
        child: items.map(
          (item) =>
            ({
              value: item[this.valueField],
              itemText: this.getItemText(item),
              selectedText: `${groupName} ${this.getItemText(item)}`,
            }) as DropdownTreeDatum,
        ),
        selectable: false,
        tooltip: 'Double click to expand',
      }
      return groupDatum
    })
  }

  private getItemText(item: T): string {
    return Array.isArray(this.textField)
      ? this.textField.map((textField) => item[textField]).join(' ')
      : (item[this.textField] as string)
  }
}
